import { LINE_SUFFIX_PATTERN, parseFileLocation, resolveFilePathWithinRoot } from '@reflex/shared';

/** Characters that make up a single path segment. */
const SEGMENT_CHARS = 'A-Za-z0-9._\\-+@';

/**
 * Regex source for an absolute POSIX path. This is what the plugin registers
 * as its host-side content scanner (`contentScanners[].pattern` on the web
 * manifest), and what {@link detectFilePaths} runs, so there is one definition
 * of what a path looks like. The `@reflex/ui` scanner registry adds the global
 * flag, so none is included here.
 *
 * Conservative on purpose — we do **not** want to linkify every `/` in prose:
 *  - the token starts at a `/` that isn't preceded by a word character, `:`,
 *    or `/`, which keeps URLs (`https://host/path`) and `and/or` out
 *  - at least one segment character after the leading separator
 *  - at least one further separator, so a bare `/foo` is not a path but
 *    `/foo/bar`, `/foo/`, and `/foo//bar` all are
 *
 * Separators are matched as runs, and a segment after one may be empty, so a
 * path written with repeated or trailing separators (`/etc//passwd`,
 * `/var/log/`) matches whole rather than being clipped to its first segment or
 * dropped entirely. Segment characters include `.`, so `.` and `..` hops match
 * as segments too (`/srv/build/../src/app.ts`). `@reflex/shared`'s
 * `resolveFilePathWithinRoot` collapses all of it before the path is used to
 * name a file — and refuses the one shape that can't be collapsed honestly, a
 * token climbing above the root; the raw token stays intact for the label so a
 * chip still reads the way the text was written. A leading run is
 * admitted too (`//etc/passwd`, the shape a path join with an empty base
 * produces), at the price of claiming a protocol-relative URL — which, with no
 * scheme to tell it apart from a rooted path, is a trade in favor of the paths
 * agents actually write.
 *
 * The repetition is safe on adversarial input: each iteration consumes exactly
 * one `/` and then a maximal run of segment characters, and the two character
 * sets are disjoint, so the number of ways to match any prefix is bounded and
 * a failing match can't blow up.
 *
 * An optional `:42` / `:42-88` line suffix is part of the match, so the
 * ubiquitous `path:line` form an agent writes in prose linkifies as one token
 * and opens the preview at those lines instead of stopping at the file. The
 * suffix pattern is shared with `@reflex/shared`'s `file-location`, which owns
 * parsing it back out.
 */
export const ABSOLUTE_PATH_SCANNER_PATTERN = `(?<![A-Za-z0-9_:/])(\\/+[${SEGMENT_CHARS}]+(?:\\/[${SEGMENT_CHARS}]*)+${LINE_SUFFIX_PATTERN})`;

const PATH_RE = new RegExp(ABSOLUTE_PATH_SCANNER_PATTERN, 'g');

/**
 * Trailing punctuation that's almost always sentence-end rather than part of
 * the token, trimmed off so we don't include the period at the end of "see
 * /etc/hosts." A separator is never trimmed, so a trimmed token still carries
 * the second separator the pattern required.
 */
const TRAILING_PUNCT = /[.,;:!?)\]}'"`]+$/;

/**
 * A token whose last segment is `..`, where the trim would be wrong: it reads
 * the two dots as a sentence end and leaves a token naming a *different*
 * directory (`/a/..` is the root; `/a/` is not). A final `.` needs no such
 * guard — trimming it leaves the same directory either way.
 */
const ENDS_WITH_PARENT_SEGMENT = /\/\.\.$/;

export interface DetectedPath {
  /** Matched token, including any line suffix (without trailing punctuation). */
  path: string;
  /** Inclusive start index in the source string. */
  start: number;
  /** Exclusive end index (start + path.length). */
  end: number;
  /** 1-based first line, when the token carried a `:line` suffix. */
  startLine?: number;
  /** 1-based last line, inclusive, when the token carried a range. */
  endLine?: number;
}

/**
 * Find every POSIX absolute path in `text`. Returns matches in order.
 *
 * Markdown reaches these through the registered content scanner rather than
 * here; this is the direct form of the same pattern, for a surface that has
 * plain text in hand and renders its own segments.
 *
 * Heuristics:
 *  - {@link ABSOLUTE_PATH_SCANNER_PATTERN} owns what counts as a path — most
 *    importantly, that a bare `/foo` is not one.
 *  - Strips a trailing run of common sentence-end punctuation.
 *  - Drops a token that climbs above the root (`/../b`, `/a/../../b`). The
 *    regex can't count `..` depth, so this is the one rule enforced after the
 *    match; `resolveFilePathWithinRoot` says why such a token is not a path.
 *  - Preserves the original index so callers can splice text safely.
 *  - Reports a `:line` / `:start-end` suffix as parsed line numbers, while
 *    leaving it in `path` so the token still round-trips to the scanner.
 */
export function detectFilePaths(text: string): DetectedPath[] {
  const matches: DetectedPath[] = [];
  for (const m of text.matchAll(PATH_RE)) {
    if (m.index === undefined) continue;
    const token = m[1];
    const raw = ENDS_WITH_PARENT_SEGMENT.test(token) ? token : token.replace(TRAILING_PUNCT, '');
    const location = parseFileLocation(raw);
    if (!resolveFilePathWithinRoot(location.path)) continue;
    matches.push({
      path: raw,
      start: m.index,
      end: m.index + raw.length,
      ...(location.startLine ? { startLine: location.startLine } : {}),
      ...(location.endLine ? { endLine: location.endLine } : {}),
    });
  }
  return matches;
}

/**
 * Split a string into alternating { kind: 'text' | 'path' } segments using
 * {@link detectFilePaths}. Useful for rendering: walk the array and wrap
 * `path` segments in a clickable element while leaving `text` segments
 * untouched.
 */
export type PathSegment = { kind: 'text'; value: string } | { kind: 'path'; value: string };

export function splitByFilePaths(text: string): PathSegment[] {
  const matches = detectFilePaths(text);
  if (matches.length === 0) return [{ kind: 'text', value: text }];
  const out: PathSegment[] = [];
  let cursor = 0;
  for (const m of matches) {
    if (m.start > cursor) {
      out.push({ kind: 'text', value: text.slice(cursor, m.start) });
    }
    out.push({ kind: 'path', value: m.path });
    cursor = m.end;
  }
  if (cursor < text.length) {
    out.push({ kind: 'text', value: text.slice(cursor) });
  }
  return out;
}
